
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { timezones } from '@/utils/profile-options';
import { PhoneInput } from '@/components/ui/phone-input';
import { useGoogleMapsGeocoding } from '@/hooks/useGoogleMapsGeocoding';

interface ContactInfoFormProps {
  practitionerId: string;
  initialData: any;
  onUpdate: () => void;
}

export default function ContactInfoForm({ practitionerId, initialData, onUpdate }: ContactInfoFormProps) {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const { geocodeAddress, updatePractitionerLocation, isGeocoding } = useGoogleMapsGeocoding();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(initialData?.phone_number || '');
  const [address, setAddress] = useState(initialData?.in_person_address || '');

  const handleAddressChange = (newAddress: string) => {
    setAddress(newAddress);
  };

  const handleAddressBlur = async () => {
    // Trigger geocoding when user finishes typing (on blur)
    if (address.trim().length > 10 && address !== initialData?.in_person_address) {
      console.log('Address changed, starting geocoding process...');
      const result = await geocodeAddress(address, practitionerId);
      
      if (result) {
        console.log('Geocoding successful, updating location...');
        // Update the practitioner's location in the database
        const success = await updatePractitionerLocation(
          practitionerId,
          result.latitude,
          result.longitude,
          result.formatted_address
        );
        
        if (success) {
          // Update the address field with the formatted address
          setAddress(result.formatted_address);
          // Trigger parent update to refresh data
          onUpdate();
          console.log('Address geocoding and update completed successfully');
        }
      } else {
        console.log('Geocoding failed or returned no results');
        toast({
          title: "Address Verification Failed",
          description: "Unable to verify the address, please check the format or try again later",
          variant: "destructive",
        });
      }
    } else if (address.trim().length <= 10) {
      console.log('Address too short for geocoding:', address);
    } else {
      console.log('Address unchanged, skipping geocoding');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      const timezoneRaw = formData.get('timezone');
      const updates: Record<string, any> = {
        phone_number: phoneNumber,
        in_person_address: address,
        default_video_tool: formData.get('default_video_tool') as string,
        updated_at: new Date().toISOString(),
      };

      if (typeof timezoneRaw === 'string' && timezoneRaw.trim()) {
        updates.timezone = timezoneRaw.trim();
      }

      const { error } = await supabase
        .from('practitioners')
        .update(updates)
        .eq('id', practitionerId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Contact information updated successfully",
      });

      onUpdate();
    } catch (error) {
      console.error('Error updating contact info:', error);
      toast({
        title: "Error",
        description: "Failed to update contact information",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('contactInformation.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="phone_number">{t('contactInformation.phoneNumber')}</Label>
            <PhoneInput
              id="phone_number"
              name="phone_number"
              value={phoneNumber}
              onChange={setPhoneNumber}
              disabled={isSubmitting}
              placeholder={t('personalInfo.phoneNumberPlaceholder')}
            />
          </div>

          <div>
            <Label htmlFor="timezone">{t('contactInformation.timezone')}</Label>
            <Select name="timezone" defaultValue={initialData?.timezone || undefined} disabled={isSubmitting}>
              <SelectTrigger>
                <SelectValue placeholder={t('contactInformation.selectTimezone')} />
              </SelectTrigger>
              <SelectContent>
                {timezones.map((timezone) => (
                  <SelectItem key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {t('contactInformation.timezoneNote')}
            </p>
          </div>


          <div>
            <Label htmlFor="in_person_address">{t('contactInformation.inPersonAddress')}</Label>
            <p className="text-sm text-gray-600 mb-2">
              {t('contactInformation.addressDescription')}
            </p>
            <div className="relative">
              <Input
                id="in_person_address"
                name="in_person_address"
                value={address}
                onChange={(e) => handleAddressChange(e.target.value)}
                onBlur={handleAddressBlur}
                disabled={isSubmitting}
                placeholder="Enter your practice address..."
              />
              {isGeocoding && (
                <div className="absolute right-3 top-2.5">
                  <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('contactInformation.addressNote')} {t('contactInformation.distanceNote')}
            </p>
          </div>

          <div>
            <Label htmlFor="default_video_tool">{t('contactInformation.defaultVideoTool')}</Label>
            <Select name="default_video_tool" defaultValue={initialData?.default_video_tool || "Google Meet"} disabled={isSubmitting}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Google Meet">Google Meet</SelectItem>
                <SelectItem value="Zoom">Zoom</SelectItem>
                <SelectItem value="Microsoft Teams">Microsoft Teams</SelectItem>
                <SelectItem value="Skype">Skype</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button 
            type="submit" 
            className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('basicInformation.saving')}
              </>
            ) : (
              t('basicInformation.saveChanges')
            )}
          </Button>

          {/* Hidden field for form submission */}
          <input type="hidden" name="phone_number" value={phoneNumber} />
        </form>
      </CardContent>
    </Card>
  );
}
